import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import sanitizeHtml from 'sanitize-html'
import { routes } from '@/routes'
import { FacebookLoginService, RESERVATION_REQUEST, type EnrollmentData, type ReservationApiResponse } from '@sevenrooms/api'
import {
  CreditCardPaymentRuleEnum,
  type ReservationRequest,
  type CheckoutData,
  type RedemptionData,
  type UpdateGuestPreferenceType,
  MessageChannelOptionsEnum,
} from '@sevenrooms/domain'
import { ReservationWidget } from '@sevenrooms/domain/constants'
import { type Field, useWatchMany } from '@sevenrooms/form'
import { useLocales, type CountryCode } from '@sevenrooms/locales'
import { useNavigation } from '@sevenrooms/navigation'
import { type PaymentCardForm, FormGMapsAddressInput, CheckoutProvider, usePaymentsContext } from '@sevenrooms/payments'
import { DateTime, legacyJSDateAdapter, Formats } from '@sevenrooms/timepiece'
import { EmbeddedMap, type GoogleJWT, type ReCAPTCHARef } from '@sevenrooms/ui-kit/core'
import { ErrorBoundary } from '@sevenrooms/ui-kit/core/ErrorBoundary'
import { Form, FormReCaptcha, Label, SubmitButton } from '@sevenrooms/ui-kit/form'
import { Box, VStack, CardSection } from '@sevenrooms/ui-kit/layout'
import {
  AgreementTerms,
  BirthdayForm,
  BookingPolicy,
  PostalCodeForm,
  PreferredLanguageForm,
  PromoCodeCard,
  RedemptionGiftCard,
  ReservationBreadcrumb,
  ReservationCheckoutGuestLayout,
  ReservationCheckoutOrderSummary,
  ReservationDetailCard,
  WidgetPortal,
  PrivateEventsSummary,
  GuestPreferences,
  PrivateEventNameForm,
} from '../../components'
import { useRecaptchaRef } from '../../contexts'
import {
  useCachedCreateReservationHold,
  useClientLogin,
  useReservationNavigation,
  useReservationsRoute,
  useVenue,
  useWidgetLanguage,
  useWidgetSettings,
  useAnalyticsSettings,
  useClientTokenData,
  useWidgetAppData,
  useLanguageStrings,
} from '../../hooks'
import { reservationWidgetMessages } from '../../reservationWidgetMessages'
import {
  type UpdateFormStateCheckout,
  useCreateReservationMutation,
  useEditReservationMutation,
  useGetVenueTagsQuery,
  useGetGuestFacingUpgradeQuery,
  useModals,
  useReservationFormState,
} from '../../store'
import {
  failedCheckout,
  finishCheckoutPage,
  GoogleReserveTrack,
  loadCheckoutPage,
  loginWithSocial,
  successfulCheckout,
  emitSuccessfulBookingPostMessage,
  trackTealium,
  transformUpgradesDataToCheckout,
} from '../../utils'
import { CheckoutContainer } from './CheckoutContainer'
import { GuestLoginPanel } from './GuestLoginPanel'
import { useCheckoutForm } from './useCheckoutForm'
import { useCheckoutTotals } from './useCheckoutTotals'
import { UserContactDetails } from './UserContactDetails'
import type { OrderAgreementTermSingleProps } from '../../components/AgreementTerms/AgreementTerm'
import type { AccordionPreferenceOption } from '../../components/GuestPreferences/AccordionInput'
import type { CamelCasedPropertiesDeep } from 'type-fest'

interface CheckoutProps {
  isModifyRoute?: boolean
}
export function Checkout({ isModifyRoute }: CheckoutProps) {
  const venueInfo = useVenue()
  const {
    venueLanguages,
    id: venueId,
    countryCode: venueCountryCode,
    urlKey: venueUrlKey,
    name: venueName,
    venueInternalName,
    gmapsEmbedUrl,
    gmapsLinkByPlaceId,
    currencyCode,
    defaultMessageChannel,
    isSmsMarketingEnabled,
    isTextMarketingOptInEnabled,
    isWhatsappEnabled,
    isGuestChannelPreferenceSelectionDisabled,
    includeEndTime,
    redemptionSystemsEnabled,
    invisibleRecaptchaEnabled,
    isPrivateEventsSettingsEnabled,
  } = venueInfo
  const widgetSettings = useWidgetSettings()
  const appData = useWidgetAppData()

  const {
    useSubmitPayment,
    PaymentCheckoutComponent,
    onBookingAttemptStatusChange,
    shouldShowCustomPaymentForm: shouldShowCustomPaymentFormContext,
    isPaymentInPopup,
    shouldShowBillingAddress,
  } = usePaymentsContext()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { push } = useNavigation()
  const { primaryVenueId, requestId, channel } = useReservationsRoute()
  const { formatMessage } = useLocales()
  const { loadedLanguage } = useLanguageStrings()
  const { showErrorModal, hideModal } = useModals()
  const analyticsSettings = useAnalyticsSettings()
  const clientInfo = useClientTokenData()
  const { id: clientToken } = clientInfo ?? {}
  const showGiftCard = widgetSettings.enableGiftCardRedemption && redemptionSystemsEnabled.includes('VALUTEC')
  const {
    formState: {
      partySize: timeSlotPartySize,
      privateEventsPartySize,
      selectedTimeSlot,
      selectedExperience,
      categories,
      agreedCustomCheckoutPolicy,
      agreedToAboveAgeConsentOn,
      agreedToBookingPolicy,
      agreedToReservationSmsOptIn,
      agreedToTailoredCommunicationOptIn,
      agreedToVenueGroupMarketingOptIn,
      agreedToVenueSpecificMarketingOptIn,
      agreedToVenueSmsMarketingOptIn,
      agreedToGroupBookingsPolicy,
      agreedToGdprDietaryOptIn,
      birthdayDay,
      birthdayMonth,
      emailAddress,
      firstName,
      lastName,
      gratuityPercentage,
      phoneNumber,
      phoneCountryCode,
      postalCode,
      preferredLanguage,
      preferredMessagingChannel,
      trackingSlug,
      actual,
      clientId,
      promoCode,
      salutation,
      champagnePreferenceTags,
      clientDietaryPreferenceTags,
      liquorPreferenceTags,
      partyDietaryPreferenceTags,
      specialOccasionTags,
      customQuestionAnswer,
      lockedFields,
      duration,
      privateEventName,
      searchTab,
    },
    updateFormState,
    clearFormState,
  } = useReservationFormState()
  const privateEventsExperienceSelected = !!selectedExperience && selectedExperience.offerType === 'PDR'
  const partySize = privateEventsExperienceSelected && privateEventsPartySize !== null ? privateEventsPartySize : timeSlotPartySize
  const groupBookingPolicy = formatMessage(reservationWidgetMessages.policyPrivateEventsBody)
  const hasGroupBookingPolicy = useMemo(
    () => (privateEventsExperienceSelected && groupBookingPolicy ? !!sanitizeHtml(groupBookingPolicy, { allowedTags: [] }).trim() : false),
    [groupBookingPolicy, privateEventsExperienceSelected]
  )
  const showGroupBookingPolicyAgreement = useMemo(
    () =>
      privateEventsExperienceSelected &&
      !!selectedTimeSlot &&
      !isPrivateEventsSettingsEnabled &&
      (hasGroupBookingPolicy || !!selectedExperience.fees?.length || typeof selectedTimeSlot.minSpend === 'number'),
    [hasGroupBookingPolicy, isPrivateEventsSettingsEnabled, privateEventsExperienceSelected, selectedExperience, selectedTimeSlot]
  )

  useEffect(() => {
    // Send the Google Analytics event for checkout page load
    loadCheckoutPage(venueUrlKey, analyticsSettings.fbPixelIdsAll, analyticsSettings.analyticsEventMapping.checkoutStarted)
  }, [analyticsSettings.analyticsEventMapping.checkoutStarted, analyticsSettings.fbPixelIdsAll, venueUrlKey])

  const languageOptions = venueLanguages.map(language => ({ id: language.value, label: language.name }))
  const { birthdayType, postalCodeType, requireDietaryTagGdprOptIn } = widgetSettings
  const recaptchaRef = useRef<ReCAPTCHARef>(null)
  const invisibleRecaptchaRef = useRecaptchaRef()
  const paymentSectionRef = useRef<HTMLDivElement | null>(null)
  const { data: upgradesData } = useGetGuestFacingUpgradeQuery(venueId)
  const [createReservation] = useCreateReservationMutation()
  const [editReservation] = useEditReservationMutation()
  const {
    baseResAmount,
    baseChargeAmount,
    dueNowAmount,
    gratuityAmount,
    promoCodeDiscountAmount,
    promoCodeName,
    serviceChargeAmount,
    subtotalAmount,
    taxAmount,
    totalAmount,
    upgradesAmounts,
    upsellAmount,
    hasSelectableGratuity,
    selectedGratuityAmount,
    giftCardDiscountAmount,
  } = useCheckoutTotals()

  const saveToLocalStorage = useCallback((value: string, key: string) => {
    window.localStorage.setItem(key, JSON.stringify(value))
  }, [])

  // time slot base amount + upsell amount
  const hasSubtotal = subtotalAmount > 0

  // This checks both bundles and added upgrades
  const hasUpgrades = upgradesAmounts.length > 0

  const onGratuityPercentageChange = useCallback(
    (value: number | null) => {
      updateFormState({ gratuityPercentage: value })
    },
    [updateFormState]
  )
  const { selectedLanguage, availableLanguageOptions } = useWidgetLanguage()
  const showLangPicker = availableLanguageOptions.length > 1
  const [, { data: reservationHoldData }] = useCachedCreateReservationHold()
  const { data: venueTags } = useGetVenueTagsQuery({ venueId, lang: selectedLanguage })

  const onPaymentComponentError = useCallback(
    (error: Error) => {
      // eslint-disable-next-line no-console
      console.error(error?.message || 'Payment init error')
      showErrorModal()
    },
    [showErrorModal]
  )

  const submitPaymentMutation = useSubmitPayment({ venueId, venueUrlKey })

  const isPaymentRequired = useMemo(() => {
    if (!selectedTimeSlot || !partySize) {
      return false
    }
    if (dueNowAmount) {
      return true
    }
    // Modify reservation flow, card was saved before
    const hasCreditCardOnFile = !!actual?.paymentsBillingProfile

    const { requireCreditCard, ccPaymentRule, ccPartySizeMin } = selectedTimeSlot
    // if there is no balance, but PaymentRule is SAVE_FOR_LATER, ask for cc if min party size is met
    return !!(
      requireCreditCard &&
      !hasCreditCardOnFile &&
      ccPaymentRule === CreditCardPaymentRuleEnum.SAVE_FOR_LATER &&
      (!ccPartySizeMin || partySize > ccPartySizeMin)
    )
  }, [selectedTimeSlot, partySize, dueNowAmount, actual?.paymentsBillingProfile])
  const shouldShowCustomPaymentForm = shouldShowCustomPaymentFormContext && isPaymentRequired

  const form = useCheckoutForm({
    agreedCustomCheckoutPolicy,
    agreedToAboveAgeConsentOn,
    agreedToBookingPolicy,
    agreedToReservationSmsOptIn,
    agreedToVenueGroupMarketingOptIn,
    agreedToVenueSpecificMarketingOptIn,
    agreedToVenueSmsMarketingOptIn,
    agreedToGroupBookingsPolicy,
    agreedToTailoredCommunicationOptIn,
    agreedToGdprDietaryOptIn,
    birthdayDay,
    birthdayMonth,
    birthdayType: widgetSettings.birthdayType,
    defaultMessageChannel,
    emailAddress,
    firstName,
    lastName,
    gratuityPercentage,
    hasReCaptcha: widgetSettings.recaptchaOn,
    isWhatsappEnabled: isWhatsappEnabled || false,
    phoneNumber,
    phoneCountryCode: phoneCountryCode || venueCountryCode,
    postalCode,
    postalCodeType: widgetSettings.postalCodeType,
    preferredLanguage: selectedLanguage || preferredLanguage,
    hasAgeToConsent: widgetSettings.aboveAgeConsentOn,
    hasCustomCheckoutPolicy: widgetSettings.customCheckoutPolicyOn,
    hasRequiredClientSelectableGratuity: hasSelectableGratuity && dueNowAmount > 0,
    hasBookingPolicy: isPaymentRequired && !showGroupBookingPolicyAgreement,
    hasGdprPolicy: requireDietaryTagGdprOptIn,
    hasBillingAddress: shouldShowBillingAddress && isPaymentRequired,
    hasCustomPaymentForm: shouldShowCustomPaymentForm,
    hasGroupBookingsPolicy: showGroupBookingPolicyAgreement,
    clientId,
    promoCode,
    salutation,
    salutationType: widgetSettings.salutationType,
    champagnePreferenceTags,
    clientDietaryPreferenceTags,
    liquorPreferenceTags,
    partyDietaryPreferenceTags,
    specialOccasionTags,
    customQuestionAnswer,
    preferredMessagingChannel,
    isPaymentRequired,
    isPaymentSectionValid: true,
    privateEventName,
  })

  const {
    field,
    setValue,
    getValues,
    formState: { errors },
    trigger,
  } = form

  const handleOnInvalid = useCallback((data: unknown) => {
    // eslint-disable-next-line no-console
    console.log(data)
  }, [])

  const hasFormErrors = !!Object.keys(errors).length

  useEffect(() => {
    // we need to update zod schema error messages when language was switched and loaded
    if (hasFormErrors && selectedLanguage === loadedLanguage) {
      trigger()
    }
  }, [trigger, loadedLanguage, hasFormErrors, selectedLanguage])

  const [agreedToVenueSmsMarketingOptInRT, agreedToReservationSmsOptInRT, billingAddress, selectedClientTags] = useWatchMany(field, [
    'agreedToVenueSmsMarketingOptIn',
    'agreedToReservationSmsOptIn',
    'billingAddress',
    'clientDietaryPreferenceTags',
  ])

  const showGdprAgreement = requireDietaryTagGdprOptIn && selectedClientTags.length

  const { instantExperiencesEnabled } = useReservationsRoute()

  const onRedemptionFieldChange = useCallback(
    (redemptionData: RedemptionData | undefined) => {
      updateFormState({ redemptionData })
    },
    [updateFormState]
  )

  useEffect(() => {
    if (billingAddress?.postalCode) {
      setValue('postalCode', billingAddress.postalCode, { shouldValidate: true })
    }
  }, [billingAddress?.postalCode, setValue])

  const onNavigatePrevious = useCallback(() => {
    updateFormState(getValues())
  }, [updateFormState, getValues])
  const { navigatePrevious, navigateToFirstStep } = useReservationNavigation({ onNavigatePrevious })
  const onClientLogin = useCallback(
    ({
      firstName,
      lastName,
      emailAddress,
      phoneCountryCode,
      phoneDialCode,
      phoneNumber,
      clientDietaryPreferenceTags,
      birthdayDay,
      birthdayMonth,
    }: {
      firstName?: string
      lastName?: string
      emailAddress?: string
      phoneNumber?: string
      phoneCountryCode?: CountryCode
      phoneDialCode?: string
      clientDietaryPreferenceTags?: string[]
      birthdayDay?: string
      birthdayMonth?: string
    }) => {
      if (firstName) {
        setValue('firstName', firstName, { shouldValidate: true })
      }
      if (lastName) {
        setValue('lastName', lastName, { shouldValidate: true })
      }
      if (emailAddress) {
        setValue('emailAddress', emailAddress, { shouldValidate: true })
      }
      if (phoneCountryCode && phoneDialCode && phoneNumber) {
        setValue('phoneNumber', phoneNumber, { shouldValidate: true })
        setValue('phoneDialCode', phoneDialCode)
        setValue('phoneCountryCode', phoneCountryCode)
      }
      if (clientDietaryPreferenceTags) {
        setValue('clientDietaryPreferenceTags', clientDietaryPreferenceTags)
      }
      if (birthdayDay) {
        setValue('birthdayDay', birthdayDay, { shouldValidate: true })
      }
      if (birthdayMonth) {
        setValue('birthdayMonth', birthdayMonth, { shouldValidate: true })
      }
    },
    [setValue]
  )

  const submitBtnLabel = useMemo(() => {
    if (isPaymentInPopup && isPaymentRequired) {
      return formatMessage(reservationWidgetMessages.proceedToPaymentLabel)
    }
    return isModifyRoute
      ? formatMessage(reservationWidgetMessages.resWidgetCheckoutModifyButtonLabel)
      : formatMessage(reservationWidgetMessages.resWidgetCheckoutSubmitButtonLabel)
  }, [formatMessage, isModifyRoute, isPaymentInPopup, isPaymentRequired])

  const {
    provider,
    setClientLoginDetails,
    userInfo: { clientId: sevenroomsClientId },
  } = useClientLogin(onClientLogin)

  const handleOnSubmit = async (data: UpdateFormStateCheckout) => {
    setIsSubmitting(true)

    if (onBookingAttemptStatusChange) {
      onBookingAttemptStatusChange(false)
    }
    updateFormState(data)
    if (!selectedTimeSlot) {
      return
    }
    const upgrades = transformUpgradesDataToCheckout(categories, upgradesData?.inventories)
    const venueGroupMarketingOptIn = widgetSettings.venueGroupMarketingOn ? data.agreedToVenueGroupMarketingOptIn : undefined
    const venueSpecificMarketingOptIn = widgetSettings.venueSpecificMarketingOn ? data.agreedToVenueSpecificMarketingOptIn : undefined
    const venueSmsMarketingOptIn = widgetSettings.venueSmsMarketingOn ? data.agreedToVenueSmsMarketingOptIn : undefined
    let { billingAddress } = data
    const { privateEventName } = data
    if (shouldShowCustomPaymentForm) {
      billingAddress = data?.payment?.billingAddress
    }

    const submitData = {
      userData: {
        globalClientId: sevenroomsClientId,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.emailAddress,
        phoneNumber: data.phoneNumber,
        phoneCountryCode: data.phoneCountryCode,
        phoneDialCode: data.phoneDialCode,
        birthdayDay: data.birthdayDay,
        birthdayMonth: data.birthdayMonth,
        preferredLanguageCode: data.preferredLanguage,
        postalCode: data.postalCode,
        socialMediaPicture: data.socialMediaPicture,
        socialMediaUserId: data.socialMediaUserId || undefined,
        socialMediaLoginSite: data.socialMediaLoginSite,
        socialMediaLocation: data.socialMediaLocation,
        // agreement to check out fields
        venueGroupMarketingOptIn,
        venueSpecificMarketingOptIn,
        venueSmsMarketingOptIn,
        marketingOptIn: venueGroupMarketingOptIn || venueSpecificMarketingOptIn,
        displayReservationSmsOptIn: widgetSettings.displayReservationSmsOptIn,
        reservationSmsOptIn: widgetSettings.displayReservationSmsOptIn ? data.agreedToReservationSmsOptIn : undefined,
        agreedToAboveAgeConsent: widgetSettings.aboveAgeConsentOn ? data.agreedToAboveAgeConsentOn : undefined,
        agreedToGroupBookingsPolicy: showGroupBookingPolicyAgreement ? data.agreedToGroupBookingsPolicy : undefined,
        agreedCustomCheckoutPolicy: widgetSettings.customCheckoutPolicyOn ? data.agreedCustomCheckoutPolicy : undefined,
        agreedToGdprDietaryOptin: showGdprAgreement ? data.agreedToGdprDietaryOptIn : undefined,
        tailoredCommunicationOptIn: widgetSettings.tailoredCommunicationOn ? data.agreedToTailoredCommunicationOptIn : undefined,
        preferredMessagingChannel: isWhatsappEnabled && data.preferredMessagingChannel ? data.preferredMessagingChannel : undefined,
        salutation: data.salutation ?? undefined,
        customQuestionResponseNote: data.customQuestionAnswer,
        trackingSlug,
      },
      reservationData: {
        shiftPersistentId: selectedTimeSlot.shiftPersistentId ?? '',
        accessPersistentId: selectedTimeSlot.accessPersistentId ?? '',
        date: selectedTimeSlot?.timeIso,
        time: selectedTimeSlot.time,
        partySize,
        venueId, // Need to change in the future for group widget to "state.venueEntities[timeSlotVenue].id"
        selectedUpsells: upgrades,
        reservationHoldId: reservationHoldData?.reservationHoldId,
        promoCodeId: data.promoCode?.id || undefined,
        recaptcha: data.reCaptcha || undefined,
        pickedDuration: duration,
        campaign: undefined, // in old widget this value taken from url "state.app.requestParams.campaign"
        clientToken,
        widgetPrimaryVenueId: primaryVenueId || venueId,
        processId: undefined, // undefined for stripe payment provider
        requestId,
        actualId: actual?.id,
        privateEventName,
      },
      chargeData: {
        chargeBaseAmount: baseChargeAmount,
        chargeUpsellAmount: upsellAmount,
        promoDiscountAmount: promoCodeDiscountAmount,
        selectedGratuityRate: data.gratuityPercentage ?? 0,
        chargeGratuity: gratuityAmount,
        chargeServiceCharge: serviceChargeAmount,
        chargeTax: taxAmount,
        chargeAmount: totalAmount,
        amountDue: dueNowAmount,
        gratuityRate: undefined, // this field not used in current widget as well
        redemptionData: data.redemptionData,
        redemptionEncryptionId: widgetSettings.redemptionCardEncryptionId,
        redemptionSystem: 'VALUTEC',
        // these fields are empty now, because used with payment providers haven't supported yet by new widget
        cardData: undefined, // new CyberEncrypt(), used for not stripe payment providers
        zip: billingAddress?.postalCode,
        address1: billingAddress?.address1,
        city: billingAddress?.city,
        locality: billingAddress?.locality,
        administrativeArea: billingAddress?.administrativeArea,
        countryCode: billingAddress?.countryCode,
        browserScreenWidth: window.innerWidth.toString(),
        browserScreenHeight: window.innerHeight.toString(),
      },
      // eslint-disable-next-line no-warning-comments
      // TODO GX-2914
      clientData: {
        trackingSlug,
        clientId,
        channel: channel || ReservationWidget.SevenRoomsWidgetChannel,
        experienceId: selectedExperience?.id,
        referralId: undefined,
        newWidgetUsed: true,
      },
      tagsData: {
        clientTags: widgetSettings.enableDietaryRestrictions ? [...data.clientDietaryPreferenceTags] : clientDietaryPreferenceTags,
        reservationTags: [
          ...data.partyDietaryPreferenceTags,
          ...data.specialOccasionTags,
          ...data.champagnePreferenceTags,
          ...data.liquorPreferenceTags,
        ],
      },
    }

    if (data.preferredLanguage) {
      saveToLocalStorage(data.preferredLanguage, ReservationWidget.PreferredLanguageCookie)
    }

    const submitBookReservation = async (submitData: ReservationRequest) => {
      const finalSubmitData = { ...submitData }

      if (invisibleRecaptchaEnabled) {
        invisibleRecaptchaRef.current.reset()
        const invisibleRecaptcha = await invisibleRecaptchaRef.current.executeAsync()

        finalSubmitData.reservationData = {
          ...finalSubmitData.reservationData,
          invisibleRecaptcha,
          ...(isPaymentRequired && {
            recaptchaPaymentTransactionId: crypto.randomUUID().toString(),
          }),
        }
      }

      if (isModifyRoute) {
        return editReservation(finalSubmitData).unwrap()
      }
      return createReservation(finalSubmitData).unwrap()
    }

    const onSuccessfulBooking = (bookingResponse: CamelCasedPropertiesDeep<ReservationApiResponse> | undefined) => {
      if (!bookingResponse) {
        return
      }

      const { token, reservationId, apiReservationId } = bookingResponse
      const selectedTimeSlotDateTime = DateTime.fromSafe(selectedTimeSlot?.timeIso)

      successfulCheckout(venueUrlKey, token, analyticsSettings.fbPixelIdsAll, analyticsSettings.analyticsEventMapping.checkoutSuccessful, {
        venue: venueName,
        venueInternalName,
        partySize,
        value: totalAmount,
        currency: currencyCode,
        merchant_id: venueId,
        reservation_date:
          analyticsSettings.isUsDateFormatForAnalytics && selectedTimeSlotDateTime
            ? legacyJSDateAdapter(selectedTimeSlotDateTime.toJsDate(), Formats.MonthDayYearByDash)
            : selectedTimeSlotDateTime?.toDateOnly().toIso(),
        reservation_time:
          analyticsSettings.isUsDateFormatForAnalytics && selectedTimeSlotDateTime
            ? legacyJSDateAdapter(selectedTimeSlotDateTime.toJsDate(), Formats.HoursMinutesTwelveHours)
            : selectedTimeSlotDateTime?.toTimeOnly().toHoursMinutesIso(),
        referenceCode: actual?.referenceCode || bookingResponse.message,
        ...(!analyticsSettings.suppressPiiForAnalytics && { email: data.emailAddress }),
        selectedTab: searchTab,
      })
      emitSuccessfulBookingPostMessage(reservationId, apiReservationId)

      GoogleReserveTrack()
      finishCheckoutPage(venueUrlKey)
      trackTealium(
        venueUrlKey,
        venueName,
        phoneCountryCode || venueCountryCode,
        selectedTimeSlotDateTime?.toDateOnly().formatFMonthNDaySWeek(),
        selectedTimeSlotDateTime?.toTimeOnly().toTimeOnly().toHoursMinutesIso(),
        partySize
      )
      push(routes.reservations.manage, {
        params: { venueKey: venueUrlKey },
        query: {
          token,
          lang: selectedLanguage,
          is_success: isModifyRoute ? undefined : 'true',
          is_success_edit: isModifyRoute ? 'true' : undefined,
          marketing_opt_in: data.agreedToVenueSpecificMarketingOptIn ? 'true' : undefined,
        },
      })
      clearFormState({ venueInfo, widgetSettings, clientInfo })
      recaptchaRef?.current?.reset()
      hideModal()
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFailure = (error?: any, options?: { keepLoading?: boolean; suppressTracking?: boolean; validationFailed?: boolean }) => {
      recaptchaRef?.current?.reset()
      // eslint-disable-next-line no-console
      console.log(error)
      const errorTitle = error?.errors?.[0] ?? formatMessage(reservationWidgetMessages.commonPaymentErrorPaymentRejectedError)
      const errorMessage = error?.errors?.[0] ? undefined : formatMessage(reservationWidgetMessages.resPaymentFailedModalHeader)

      if (options?.validationFailed) {
        setIsSubmitting(false)

        // Adding a bit of offset for better UI when we're scrolled up to the payments section
        if (paymentSectionRef.current) {
          const offset = 200
          const elementPosition = paymentSectionRef.current.getBoundingClientRect().top + window.pageYOffset
          const offsetPosition = elementPosition - offset

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          })
        }

        return
      }

      showErrorModal(errorTitle, errorMessage)

      if (!options?.suppressTracking) {
        failedCheckout(venueUrlKey, errorMessage ?? 'Checkout Failed')
      }
      if (error?.origin === RESERVATION_REQUEST) {
        if (onBookingAttemptStatusChange) {
          onBookingAttemptStatusChange(true)
        }
      }
      if (!options?.keepLoading) {
        setIsSubmitting(false)
      }
    }

    setIsSubmitting(true)
    try {
      if (!isPaymentRequired) {
        const response = await submitBookReservation(submitData as ReservationRequest)
        onSuccessfulBooking(response)
      } else {
        await submitPaymentMutation({
          amountDue: dueNowAmount,
          data: submitData as CheckoutData,
          submitBookReservation: submitBookReservation as unknown as (
            submitData: CheckoutData
          ) => Promise<CamelCasedPropertiesDeep<ReservationApiResponse> | undefined>,
          onSuccessfulBooking: onSuccessfulBooking as unknown as (
            bookingResponse: { token: string; enrollmentResult?: EnrollmentData } | undefined
          ) => void,
          setIsSubmitting,
          onFailure,
          getValues,
        })
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      onFailure(error)
    }
  }

  const onLoginFB = useCallback(async () => {
    const data = await FacebookLoginService.login()
    setClientLoginDetails('facebook', { firstName: data.firstName, lastName: data.lastName, emailAddress: data.email })
    loginWithSocial(venueUrlKey, 'facebook')
    if (data.location?.name) {
      setValue('socialMediaLocation', data.location.name)
    }
    setValue('socialMediaUserId', data.id)
    setValue('socialMediaPicture', data.picture?.data.url)
    setValue('socialMediaLoginSite', 'facebook')
  }, [setClientLoginDetails, venueUrlKey, setValue])

  const onLoginGoogle = useCallback(
    (data: GoogleJWT) => {
      setClientLoginDetails('google', { firstName: data.givenName, lastName: data.familyName, emailAddress: data.email })
      setValue('socialMediaUserId', appData.googleApiClientId)
      setValue('socialMediaPicture', data.picture)
      setValue('socialMediaLoginSite', 'google')
      loginWithSocial(venueUrlKey, 'google')
    },
    [setClientLoginDetails, setValue, appData.googleApiClientId, venueUrlKey]
  )

  const onTagsSave = useCallback(
    (options: AccordionPreferenceOption[], type: UpdateGuestPreferenceType) => {
      setValue(
        type,
        options.filter(value => value.selected).map(value => value.id),
        { shouldValidate: true }
      )
      return Promise.resolve(true)
    },
    [setValue]
  )
  const onCustomQuestionSave = useCallback(
    (value?: string) => {
      setValue('customQuestionAnswer', value ?? '', { shouldValidate: true })
      return Promise.resolve(true)
    },
    [setValue]
  )

  const agreementTerms = useMemo(() => {
    const agreements: OrderAgreementTermSingleProps[] = []
    if (showGdprAgreement) {
      agreements.push({
        field: field.prop('agreedToGdprDietaryOptIn'),
        term: `${formatMessage(reservationWidgetMessages.resWidgetGdprPolicyOptInLabel)}*`,
        infoTitle: formatMessage(reservationWidgetMessages.resWidgetCustomGdprPolicyLinkLabel),
        infoContent: formatMessage(reservationWidgetMessages.resWidgetGdprOptInLabel),
        dataTest: 'sr-agreed-to-gdpr-policy',
      })
    }
    if (showGroupBookingPolicyAgreement) {
      agreements.push({
        field: field.prop('agreedToGroupBookingsPolicy'),
        term: `${formatMessage(reservationWidgetMessages.resWidgetPrivateEventsGroupBookingOptInLabel)}*`,
        infoTitle: hasGroupBookingPolicy ? formatMessage(reservationWidgetMessages.policyPrivateEventsLabel) : undefined,
        infoContent: hasGroupBookingPolicy ? groupBookingPolicy : undefined,
        dataTest: 'sr-agreed-to-group-bookings-policy',
      })
    }

    if (isPaymentRequired && !showGroupBookingPolicyAgreement) {
      agreements.push({
        field: field.prop('agreedToBookingPolicy'),
        term: `${formatMessage(reservationWidgetMessages.resWidgetCancelPolicyOptInLabel)}*`,
        infoTitle: formatMessage(reservationWidgetMessages.resWidgetCancellationPolicyHeader),
        infoContent: selectedTimeSlot?.cancellationPolicy,
        dataTest: 'sr-agreed-to-booking-policy',
      })
    }

    if (widgetSettings.customCheckoutPolicyOn) {
      agreements.push({
        field: field.prop('agreedCustomCheckoutPolicy'),
        term: `${formatMessage(reservationWidgetMessages.resWidgetCustomPolicyOptInLabel)}*`,
        infoTitle: formatMessage(reservationWidgetMessages.resWidgetCustomPolicyHeader),
        infoContent: formatMessage(reservationWidgetMessages.policyCustomCheckout),
        dataTest: 'sr-agreed-to-custom-checkout-policy',
      })
    }

    if (widgetSettings.aboveAgeConsentOn) {
      agreements.push({
        field: field.prop('agreedToAboveAgeConsentOn'),
        term: `${formatMessage(reservationWidgetMessages.resWidgetAgeConsentLabel)} ${widgetSettings.ageToConsent}*`,
        dataTest: 'sr-agreed-to-above-age-consent-on',
      })
    }

    if (widgetSettings.venueSmsMarketingOn && (isSmsMarketingEnabled || isTextMarketingOptInEnabled)) {
      agreements.push({
        field: field.prop('agreedToVenueSmsMarketingOptIn'),
        term: formatMessage(reservationWidgetMessages.resWidgetCheckoutVenueSmsOptIn, { venue: venueName }, { ignoreTag: true }),
        infoTitle: formatMessage(reservationWidgetMessages.resWidgetMarketingPolicyHeader),
        infoContent: formatMessage(reservationWidgetMessages.policyVenueSpecificSmsMarketing, { venue: venueName }, { ignoreTag: true }),
        dataTest: 'sr-agreed-to-venue-sms-marketing-opt-in',
      })
    }

    if (widgetSettings.venueSpecificMarketingOn && (widgetSettings.showEmailMarketingOptInOnCheckoutPage ?? true)) {
      agreements.push({
        field: field.prop('agreedToVenueSpecificMarketingOptIn'),
        term: formatMessage(reservationWidgetMessages.resWidgetCheckoutVenueOptIn, { venue: venueName }, { ignoreTag: true }),
        infoTitle: formatMessage(reservationWidgetMessages.resWidgetMarketingPolicyHeader),
        infoContent: formatMessage(reservationWidgetMessages.policyVenueSpecificMarketing, { venue: venueName }, { ignoreTag: true }),
        dataTest: 'sr-agreed-to-venue-specific-marketing-on',
      })
    }

    if (widgetSettings.venueGroupMarketingOn) {
      agreements.push({
        field: field.prop('agreedToVenueGroupMarketingOptIn'),
        term: formatMessage(reservationWidgetMessages.resWidgetCheckoutVenueGroupOptIn, { venue: venueName }, { ignoreTag: true }),
        infoTitle: formatMessage(reservationWidgetMessages.resWidgetMarketingPolicyHeader),
        infoContent: formatMessage(reservationWidgetMessages.policyVenueGroupMarketing, { venue: venueName }, { ignoreTag: true }),
        dataTest: 'sr-agreed-to-venue-group-marketing-opt-in',
      })
    }

    if (widgetSettings.displayReservationSmsOptIn) {
      agreements.push({
        field: field.prop('agreedToReservationSmsOptIn'),
        term: formatMessage(reservationWidgetMessages.policyUsTwilioSmsOptIn),
        dataTest: 'sr-agreed-to-reservation-sms-opt-in',
        subOptions:
          isWhatsappEnabled && !isGuestChannelPreferenceSelectionDisabled
            ? {
                field: field.prop('preferredMessagingChannel'),
                options: [
                  {
                    label: formatMessage(reservationWidgetMessages.resWidgetPreferredChannelWhatsAppLabel),
                    value: MessageChannelOptionsEnum.WHATSAPP,
                  },
                  {
                    label: formatMessage(reservationWidgetMessages.resWidgetPreferredChannelSmsLabel),
                    value: MessageChannelOptionsEnum.SMS_MMS,
                  },
                ],
              }
            : undefined,
      })
    }

    if (widgetSettings.tailoredCommunicationOn) {
      agreements.push({
        field: field.prop('agreedToTailoredCommunicationOptIn'),
        term: formatMessage(reservationWidgetMessages.textTailoredCommunicationOptInLabel),
        dataTest: 'sr-agreed-tailored-communication-opt-in',
      })
    }
    return agreements
  }, [
    showGdprAgreement,
    showGroupBookingPolicyAgreement,
    isPaymentRequired,
    widgetSettings.customCheckoutPolicyOn,
    widgetSettings.aboveAgeConsentOn,
    widgetSettings.venueSmsMarketingOn,
    widgetSettings.venueSpecificMarketingOn,
    widgetSettings.venueGroupMarketingOn,
    widgetSettings.displayReservationSmsOptIn,
    widgetSettings.tailoredCommunicationOn,
    widgetSettings.ageToConsent,
    isSmsMarketingEnabled,
    isTextMarketingOptInEnabled,
    isWhatsappEnabled,
    isGuestChannelPreferenceSelectionDisabled,
    field,
    formatMessage,
    hasGroupBookingPolicy,
    groupBookingPolicy,
    selectedTimeSlot?.cancellationPolicy,
    venueName,
    widgetSettings.showEmailMarketingOptInOnCheckoutPage,
  ])

  const onMapClick = useMemo(() => {
    if (!gmapsLinkByPlaceId) {
      // we return undefined here, so that the EmbeddedMap component doesn't render the click wrapper
      return undefined
    }
    const clickFunc = () => {
      window.open(gmapsLinkByPlaceId, '_blank')
    }
    return clickFunc
  }, [gmapsLinkByPlaceId])

  if (!selectedTimeSlot) {
    navigateToFirstStep()
    return null
  }

  return (
    <CheckoutContainer
      reservationDetails={
        <ReservationDetailCard
          guestCount={partySize}
          oldMaxGuest={actual?.maxGuests}
          reservationDuration={includeEndTime ? selectedTimeSlot.duration : undefined}
          oldReservationDuration={includeEndTime ? actual?.duration : undefined}
          reservationTime={selectedTimeSlot?.timeIso}
          oldReservationTime={actual?.arrivalTime}
          reservationDate={selectedTimeSlot?.timeIso}
          oldReservationDate={actual?.date}
          timeSlotDescription={selectedTimeSlot?.publicTimeSlotDescription}
          oldTimeSlotDescription={actual?.timeSlotDescription}
          imageUrl={`/.h/download/${
            widgetSettings.headerImgPhotoDict?.mega ?? widgetSettings.headerImgPhotoDict?.medium ?? widgetSettings.headerImgPhotoDict?.large
          }`}
          venueName={venueName}
          fallback={
            gmapsEmbedUrl ? (
              <EmbeddedMap clickTestId="venue-map" embeddedMapUrl={gmapsEmbedUrl} height="240px" width="100%" onMapClick={onMapClick} />
            ) : (
              <></>
            )
          }
        />
      }
    >
      <Form {...form} onSubmit={handleOnSubmit} onInvalid={handleOnInvalid} autoComplete="on">
        <VStack spacing="m">
          <ReservationCheckoutGuestLayout
            p="lm"
            socialMediaSection={
              !!provider || isModifyRoute || instantExperiencesEnabled ? null : (
                <GuestLoginPanel
                  googleApiClientId={appData.googleApiClientId}
                  selectedLanguage={selectedLanguage}
                  onFacebookClick={onLoginFB}
                  onGoogleLogin={onLoginGoogle}
                  disabled={isSubmitting}
                  socialMediaLoginEnabled={widgetSettings.enableSocialMediaLogin && !clientToken}
                />
              )
            }
            spacing="m"
          >
            <VStack spacing="m">
              <UserContactDetails field={field} lockedFields={lockedFields} disabled={isSubmitting} />
              {birthdayType !== 'Hidden' && (
                <BirthdayForm
                  required={birthdayType === 'Required'}
                  disabled={isSubmitting}
                  dayField={field.prop('birthdayDay')}
                  monthField={field.prop('birthdayMonth')}
                />
              )}
              {postalCodeType !== 'Hidden' && (
                <PostalCodeForm
                  required={postalCodeType === 'Required'}
                  postalCodeField={field.prop('postalCode')}
                  disabled={lockedFields.postalCode || isSubmitting}
                />
              )}
              {showLangPicker && (
                <PreferredLanguageForm languageField={field.prop('preferredLanguage')} options={languageOptions} disabled={isSubmitting} />
              )}
              {shouldShowBillingAddress && isPaymentRequired && (
                <Label primary={`${formatMessage(reservationWidgetMessages.billingAddressLabel)}*`}>
                  <FormGMapsAddressInput
                    aria-required="true"
                    data-test="sr-reservation-billing-address"
                    disabled={isSubmitting}
                    field={field.prop('billingAddress')}
                    fullWidth
                    placeholder={formatMessage(reservationWidgetMessages.billingAddressEnterYourAddressLabel)}
                    googlePlacesApiKey={appData.googlePlacesApiKey}
                  />
                </Label>
              )}
              {privateEventsExperienceSelected && <PrivateEventNameForm privateEventNameField={field.prop('privateEventName')} />}
            </VStack>
          </ReservationCheckoutGuestLayout>

          {isPaymentRequired && (
            <div ref={paymentSectionRef}>
              <CardSection>
                <ErrorBoundary fallback={<></>} onError={onPaymentComponentError}>
                  <input type="hidden" name="cc_holder_name" />
                  <CheckoutProvider
                    value={{
                      amount: dueNowAmount,
                      isSubmitting,
                      setIsSubmitting,
                      venueId,
                      venueName,
                      googlePlacesApiKey: appData.googlePlacesApiKey,
                      resWidgetPaymentLabel: formatMessage(reservationWidgetMessages.resWidgetPaymentLabel),
                      paymentField: shouldShowCustomPaymentForm ? (field.prop('payment') as Field<PaymentCardForm>) : undefined,
                      isPaymentSectionValidField: field.prop('isPaymentSectionValid'),
                    }}
                  >
                    <PaymentCheckoutComponent />
                  </CheckoutProvider>
                </ErrorBoundary>
              </CardSection>
            </div>
          )}
          {hasSubtotal && (
            <>
              {widgetSettings.enablePromoCodes && (
                <PromoCodeCard field={field.prop('promoCode')} dateTimeIso={selectedTimeSlot?.timeIso ?? ''} disabled={isSubmitting} />
              )}
              {showGiftCard && (
                <RedemptionGiftCard
                  field={field.prop('redemptionData')}
                  onChange={onRedemptionFieldChange}
                  totalAmount={totalAmount}
                  disabled={isSubmitting}
                />
              )}
            </>
          )}

          {(hasUpgrades || hasSubtotal) && (
            <ReservationCheckoutOrderSummary
              seatingAreaDescription={selectedTimeSlot?.publicTimeSlotDescription}
              guestCount={partySize}
              baseAmount={baseResAmount}
              gratuityAmount={gratuityAmount}
              gratuityPercentageField={field.prop('gratuityPercentage')}
              onGratuityPercentageChange={onGratuityPercentageChange}
              hasSelectableGratuity={hasSelectableGratuity}
              selectedGratuityAmount={selectedGratuityAmount}
              promoCodeDiscountAmount={promoCodeDiscountAmount}
              promoCodeDisplayName={promoCodeName}
              serviceChargeAmount={serviceChargeAmount}
              subtotalAmount={subtotalAmount}
              taxAmount={taxAmount}
              upgradesAmounts={upgradesAmounts}
              venueCurrencyCode={currencyCode}
              disabled={isSubmitting}
              giftCardDiscountAmount={giftCardDiscountAmount}
              dueNowAmount={dueNowAmount}
            />
          )}

          {privateEventsExperienceSelected && (
            <PrivateEventsSummary
              minSpend={selectedTimeSlot.minSpend ?? undefined}
              currencyCode={currencyCode}
              fees={selectedExperience.fees}
              showPrivateEventsPolicy={!isPrivateEventsSettingsEnabled}
              cancellationPolicy={isPaymentRequired ? selectedTimeSlot?.cancellationPolicy : undefined}
              bookingCheckoutPolicy={selectedTimeSlot?.policy}
            />
          )}
          {widgetSettings.recaptchaOn && (
            <FormReCaptcha
              ref={recaptchaRef}
              field={field.prop('reCaptcha')}
              siteKey={widgetSettings.recaptchaSiteKey}
              locale={selectedLanguage}
            />
          )}
          <VStack mt="s" mb="m" spacing="m">
            <GuestPreferences
              guestCount={partySize}
              venueTags={venueTags}
              showDietaryRestrictions={widgetSettings.enableDietaryRestrictions}
              showCustomQuestion={widgetSettings.enableCustomQuestion}
              // we don't show image input on checkout page for now
              showGuestImageInput={false}
              showSpecialOccasions={widgetSettings.enableSpecialOccasions}
              champagnePreferenceTags={champagnePreferenceTags}
              clientDietaryPreferenceTags={clientDietaryPreferenceTags}
              liquorPreferenceTags={liquorPreferenceTags}
              partyDietaryPreferenceTags={partyDietaryPreferenceTags}
              specialOccasionTags={specialOccasionTags}
              otherInfoText={customQuestionAnswer}
              onCustomQuestionSave={onCustomQuestionSave}
              onTagSave={onTagsSave}
            />
            <Box mt="s">
              <AgreementTerms agreements={agreementTerms} disabled={isSubmitting} />
            </Box>
          </VStack>
          <VStack spacing="m">
            <SubmitButton variant="primary" data-test="checkout-button-complete" fullWidth isSubmitting={isSubmitting}>
              {submitBtnLabel}
            </SubmitButton>
            <BookingPolicy
              termsPolicyHolderName={widgetSettings.termsPolicyHolderName}
              termsOfServiceUrl={widgetSettings.termsOfServiceUrl}
              customPrivacyPolicyLink={widgetSettings.textCustomPrivacyPolicyLink}
              customPolicyHolderName={widgetSettings.textCustomPolicyHolderName}
              privacyPolicyUrl={widgetSettings.privacyPolicyUrl}
              privacyPolicyText={widgetSettings.privacyPolicyText}
              showSevenRoomsPolicy={widgetSettings.isSevenroomsPolicyEnabled}
              showSmsPolicy={agreedToReservationSmsOptInRT || agreedToVenueSmsMarketingOptInRT}
            />
          </VStack>
        </VStack>
      </Form>
      <WidgetPortal portalId="checkout-breadcrumbs">
        <ReservationBreadcrumb
          venueName={venueName}
          partySize={partySize}
          timeIso={selectedTimeSlot?.timeIso}
          holdEndTime={reservationHoldData?.holdEndTime}
          onClick={() => {
            navigatePrevious()
          }}
          navigationDisabled={isSubmitting}
          label={isModifyRoute ? formatMessage(reservationWidgetMessages.resWidgetNewReservationDetailsLabel) : undefined}
          timeSlotDescription={selectedTimeSlot?.publicTimeSlotDescription}
        />
      </WidgetPortal>
    </CheckoutContainer>
  )
}
